import { useFormik } from 'formik';
import debounce from 'lodash.debounce';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';

import { Button } from '~/atomic/atom/Button';
import { Tag } from '~/atomic/atom/Tag';
import { Field } from '~/atomic/molecula/Field';
import Modal from '~/atomic/molecula/Modal';
import { Select } from '~/atomic/molecula/Select';
import { bookModerationUpdate } from '~/atomic/page/book/Book/bookModeration/bookModeration.data';
import { bookModerationSelector } from '~/atomic/page/book/Book/bookModeration/bookModeration.selector';
import { bookModerationActions } from '~/atomic/page/book/Book/bookModeration/bookModeration.slice';
import { disabledGenresForSelect } from '~/atomic/page/book-edit/DescriptionInputs/DescriptionInputs';
import { bookSelector } from '~/feature/book/book.selector';
import { genres } from '~/feature/genre/genres';
import { searchTag } from '~/feature/tag/tag.data';
import { tagSelector } from '~/feature/tag/tag.selector';
import { useAppDispatch, useAppSelector } from '~/store';

export const BookModerationModal = () => {
  const dispatch = useAppDispatch();
  const {
    isOpenModerationBookModal,
    isSavePropertiesLoading,
  } = useAppSelector(bookModerationSelector);
  const { tags } = useAppSelector(tagSelector);
  const { bookPage } = useAppSelector(bookSelector);
  const closeModal = () => {
    dispatch(bookModerationActions.setIsOpenModerationBookModal(false));
  };

  const searchTags = debounce(
    (name: string) => {
      if (name.length > 0) {
        dispatch(searchTag({ name }));
      }
    },
    300,
  );

  const genresForSelect = genres.map((genre) => {
    if (disabledGenresForSelect.includes(genre.slug)) {
      return {
        value: genre.slug,
        label: genre.name,
        disabled: true,
      };
    }

    return {
      value: genre.slug,
      label: genre.name,
    };
  });

  const isFunfikIncludes = (array: string[]) => {
    return array.some((genre) => {
      return genre.includes('fanfik');
    });
  };

  const moderationForm = useFormik({
    initialValues: {
      genres: bookPage?.genres
        ?.map((genre) => genre.slug) ?? [],
      tags: bookPage?.tags
        ?.map((tag) => tag.name) ?? [],
    },
    validateOnBlur: false,
    validationSchema: Yup.object({
      genres: Yup
        .array()
        .of(Yup.string())
        .required()
        .test('isFanfic', 'ФанФик не может быть платным', (value) => {
          if (bookPage?.free) return true;
          return !isFunfikIncludes(value);
        })
        .max(3, 'Максимальное количество жанров 3'),
      tags: Yup
        .array<string>()
        .max(14, 'Превышено максимальное количество тегов')
        .of(
          Yup.string()
            .min(2, 'Минимальная длина тега - 2 символа')
            .max(40, 'Максимальная длина тега - 40 символа')
            .test('check special symbols', 'Неверных формат тега', (value) => {
              return !value.match(/[^a-z^а-яё^\d^\s^-]/gi);
            }),
        )
        .required(),
    }),
    onSubmit: async (values) => {
      await dispatch(bookModerationUpdate(values));
      closeModal();
    },
  });

  useEffect(() => {
    moderationForm.setValues({
      genres: bookPage?.genres
        ?.map((genre) => genre.slug) ?? [],
      tags: bookPage?.tags
        ?.map((tag) => tag.name) ?? [],
    });
  }, [bookPage]);

  return (
    <Modal
      open={isOpenModerationBookModal}
      onCancel={closeModal}
      title="Модерирование книги"
    >
      <SCForm
        onSubmit={moderationForm.handleSubmit}
      >
        <Field
          label="Жанры*"
          tip="до 3 жанров"
          error={moderationForm.touched.genres && moderationForm.errors.genres as string}
        >
          <SCMultiplySelect
            mode="tags"
            maxTagCount={3}
            onBlur={moderationForm.handleBlur}
            onChange={(value) => {
              moderationForm.setFieldValue('genres', value);
            }}
            value={moderationForm.values.genres}
            showArrow
            tagRender={(props) => {
              return (
                <Tag
                  {...props}
                  closable
                  className="ant-select-selection-item"
                  style={{ alignItems: 'center' }}
                >
                  {props.label}
                </Tag>
              );
            }}
            placeholder="Выберите жанр произведения"
            optionFilterProp="label"
            options={
              moderationForm.values.genres.length > 2
                ? genres
                  .filter((genre) => moderationForm.values.genres.includes((genre.slug)))
                  .map((genre) => ({ value: genre.slug, label: genre.name }))
                : genresForSelect
            }
          />
        </Field>
        <Field
          label="Теги*"
          error={moderationForm.touched.tags && moderationForm.errors.tags as string}
        >
          <SCMultiplySelect
            filterOption
            mode="tags"
            placeholder="Добавьте тег..."
            onSearch={searchTags}
            value={moderationForm.values.tags}
            onChange={(value) => {
              moderationForm.setFieldValue('tags', value);
            }}
            onBlur={moderationForm.handleBlur}
            options={tags.map((tag) => {
              return ({ value: tag.name, title: tag.name });
            })}
          />
        </Field>
        <SCSaveFormButton
          isLoading={isSavePropertiesLoading}
          htmlType="submit"
          type="primary"
          size="large"
        >
          Сохранить
        </SCSaveFormButton>
      </SCForm>
    </Modal>
  );
};

const SCMultiplySelect = styled(Select)`
  width: 100%;
  min-height: 44px;

  .ant-select-selector {
    min-height: 44px !important;
  }
`;

const SCSaveFormButton = styled(Button)`
  margin-top: 80px;
  margin-left: auto;
  margin-right: auto;
`;

const SCForm = styled.form`
  > *:not(:last-child) {
    margin-bottom: 25px;
  }
`;
